'use client';

/**
 * A link the shopper sent, as a card in their turn of the conversation:
 * the product's photo, title, brand and site, its price, and a way out.
 *
 *  - Amazon: the price only with the time Amazon's API gave it and Amazon's
 *    disclaimer one tap away (Associates licence, same line as the shelf), and
 *    "View on Amazon" through our /go hop (the route built it: the partner
 *    tag is added there, for a verified in-market human only). In the native
 *    app, where Amazon links are suppressed, neither the price nor the button
 *    is shown.
 *  - Any other site: the price the page published, and "Open page".
 *  - A page that could not be read: its address, and "Open page".
 *
 * The preview comes from the same store as the composer chip
 * (lib/link-preview), so one that lands after the send still fills the card.
 */

import React from 'react';
import { HiDocumentText as NoteIcon, HiLink as LinkIcon } from 'react-icons/hi';
import type { LocaleDetails } from 'shared/src/types/languages.type';
import type { LinkPreview } from 'shared/src/types/link-preview.type';
import { getSearchUiContent } from '@/lib/ui-content/search-ui-content';
import { uiContent } from '@/lib/ui-content/ui-content';
import { ObservedStamp } from '@/components/products/links/observed-stamp';
import { AMAZON_PRICE_DISCLAIMER_FALLBACK } from '@/components/products/links/offers/offer-copy';
import { AffiliateInfoIcon } from '@/components/products/links/affiliate-info-icon';
import { ExternalAnchor } from '@/components/products/links/external-anchor';
import { useNativeApp } from '@/components/runtime/native-app-context';
import { shouldSuppressAmazonLinks } from '@/lib/native/amazon-app-policy';
import { useLinkPreview } from '@/lib/link-preview/use-link-preview';

const BUTTON_CLASS =
  'inline-flex h-7 items-center rounded-full border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-white/15 dark:bg-white/5 dark:text-zinc-200 dark:hover:bg-white/10';

/** A price in its own currency, written the shopper's way. */
export function formatLinkPrice(amount: number, currency: string | null, locale: Pick<LocaleDetails, 'path'>): string | null {
  if (!currency || !Number.isFinite(amount)) return null;
  try {
    return new Intl.NumberFormat(String(locale.path).replace(/-UK$/, '-GB'), { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

/** Associates licence: an Amazon price is never shown once it is a day old. */
const AMAZON_PRICE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function isFreshAmazonPrice(observedAt: string, now: number): boolean {
  const at = Date.parse(observedAt);
  return Number.isFinite(at) && now - at <= AMAZON_PRICE_MAX_AGE_MS && at - now < 5 * 60 * 1000;
}

function truncateUrl(url: string, max = 60): string {
  const bare = url.replace(/^https?:\/\/(www\.)?/, '');
  return bare.length > max ? `${bare.slice(0, max - 1)}…` : bare;
}

export function LinkAttachmentCardView({
  url,
  host,
  locale,
  status,
  preview,
  suppressAmazon,
  now = Date.now(),
}: {
  url: string;
  host: string;
  locale: LocaleDetails;
  status: 'idle' | 'loading' | 'ready' | 'unavailable' | 'forbidden';
  preview: LinkPreview | null;
  suppressAmazon: boolean;
  /** Test seam for the price's age check. */
  now?: number;
}) {
  const content = getSearchUiContent(locale);
  const loading = status === 'idle' || status === 'loading';
  const readable = preview && preview.kind !== 'unavailable' ? preview : null;
  const title = readable?.title ?? null;
  const image = readable?.image ?? null;
  const brand = readable?.brand ?? null;
  const isAmazon = preview?.kind === 'amazon';

  let priceText: string | null = null;
  let observedAt: string | null = null;
  if (preview?.kind === 'amazon' && preview.price && !suppressAmazon && isFreshAmazonPrice(preview.price.observedAt, now)) {
    priceText = formatLinkPrice(preview.price.amount, preview.price.currency, locale);
    observedAt = preview.price.observedAt;
  } else if ((preview?.kind === 'product' || preview?.kind === 'page') && preview.price) {
    priceText = formatLinkPrice(preview.price.amount, preview.price.currency, locale);
  }

  let action: { href: string; label: string; rel: string } | null = null;
  if (preview?.kind === 'amazon') {
    if (!suppressAmazon && preview.outboundHref) {
      // No `noreferrer`: Amazon must be able to see the site a click came from
      // (Associates Participation Requirements 6(v)), same as every other buy link.
      action = { href: preview.outboundHref, label: content.viewOnAmazon, rel: 'nofollow sponsored noopener' };
    }
  } else if (!loading) {
    const href = (readable && 'canonical' in readable && readable.canonical) || url;
    action = { href, label: content.openPage, rel: 'nofollow noopener noreferrer' };
  }

  return (
    <div
      className="w-[20rem] max-w-full rounded-card border border-zinc-200/70 bg-white/80 p-2.5 text-left dark:border-white/10 dark:bg-white/[0.04]"
      data-link-card=""
      data-link-url={url}
      aria-busy={loading || undefined}
    >
      <div className="flex gap-2.5">
        <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-nested bg-white text-zinc-400 ring-1 ring-zinc-200/70 dark:bg-white/90 dark:ring-white/10">
          {image ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={image} alt="" className="h-full w-full object-contain" referrerPolicy="no-referrer" loading="lazy" />
          ) : (
            <LinkIcon className={`h-5 w-5 ${loading ? 'animate-pulse' : ''}`} aria-hidden="true" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          {loading ? (
            <>
              <span className="sr-only">{content.linkLoading}</span>
              <div className="mt-0.5 h-3.5 w-11/12 animate-pulse rounded bg-zinc-200/80 dark:bg-white/10" />
              <div className="mt-1.5 h-3.5 w-2/3 animate-pulse rounded bg-zinc-200/80 dark:bg-white/10" />
              <p className="mt-1.5 truncate text-xs text-zinc-500 dark:text-zinc-400">{host}</p>
            </>
          ) : (
            <>
              <p className="line-clamp-2 text-sm font-medium leading-snug text-zinc-800 dark:text-zinc-100">
                {title ?? truncateUrl(url)}
              </p>
              <p className="mt-0.5 truncate text-xs text-zinc-500 dark:text-zinc-400">
                {[brand, host].filter(Boolean).join(' · ')}
                {!readable && ` · ${content.linkUnavailable}`}
              </p>
              {priceText && (
                <p className="mt-1 text-sm font-semibold tabular-nums text-zinc-900 dark:text-white">{priceText}</p>
              )}
              {isAmazon && observedAt && (
                <span className="flex items-center gap-1 text-[0.62rem] leading-tight text-zinc-400 dark:text-zinc-500">
                  <span>
                    <ObservedStamp
                      iso={observedAt}
                      template={uiContent[locale.languageCode]?.amazonPriceAsOf ?? 'Amazon price · {time}'}
                      localeDetails={locale}
                    />
                  </span>
                  <AffiliateInfoIcon
                    languageCode={locale.languageCode}
                    note={uiContent[locale.languageCode]?.amazonPriceDisclaimer ?? AMAZON_PRICE_DISCLAIMER_FALLBACK}
                  />
                </span>
              )}
            </>
          )}
        </div>
      </div>
      {action && (
        <div className="mt-2 flex justify-end">
          <ExternalAnchor href={action.href} rel={action.rel} className={BUTTON_CLASS}>
            {action.label}
          </ExternalAnchor>
        </div>
      )}
    </div>
  );
}

/** The card, live: reads (and if needed asks for) the link's preview. */
export function LinkAttachmentCard({
  url,
  host,
  locale,
  snapshot,
}: {
  url: string;
  host: string;
  locale: LocaleDetails;
  snapshot?: LinkPreview | null;
}) {
  const { isAppOrigin } = useNativeApp();
  const { status, preview } = useLinkPreview(url, {
    snapshot,
    fetchWith: { locale: locale.path, path: `/${locale.path}` },
  });
  return (
    <LinkAttachmentCardView
      url={url}
      host={host}
      locale={locale}
      status={status}
      preview={preview}
      suppressAmazon={shouldSuppressAmazonLinks(isAppOrigin)}
    />
  );
}

/** A note the shopper sent, quoted in their turn. */
export function NoteAttachmentBlock({ text, locale }: { text: string; locale: LocaleDetails }) {
  const content = getSearchUiContent(locale);
  return (
    <figure className="w-fit max-w-full rounded-2xl rounded-br-md bg-zinc-100/70 px-3.5 py-2 dark:bg-white/10" data-note-block="">
      <figcaption className="mb-1 flex items-center gap-1 font-ui text-[0.68rem] uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
        <NoteIcon className="h-3 w-3" aria-hidden="true" />
        {content.attachNote}
      </figcaption>
      <blockquote className="max-h-40 overflow-y-auto whitespace-pre-wrap border-l-2 border-zinc-300 pl-2.5 text-sm leading-relaxed text-zinc-700 dark:border-white/20 dark:text-zinc-200">
        {text}
      </blockquote>
    </figure>
  );
}
